import { useState } from 'react';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  SR_API_ACTION_ADDRESS_RESOLUTION_ATTEMPTS_BULK_IGNORE,
  SR_API_ACTION_LISTINGS_VERIFICATION_BULK_ARCHIVE,
} from 'api/utilityActionTypes';

export const TASK_TYPE_ADDRESS_RESOLUTION = 'address_resolution';
export const TASK_TYPE_ADDRESS_VERIFICATION = 'address_verification';

const GENERIC_FAILURE_REASON = 'Request failed — please retry';

/**
 * Controller hook for the bulk-ignore flow on the Address Resolution Dashboard.
 *
 * Selection is supplied by the parent table as a Map keyed by `${taskType}:${id}`
 * (composite key required because resolution attempts and listings share the same
 * id space). The hook splits the selection by task type and fires two independent
 * bulk requests in parallel — one per domain — then merges the per-id outcomes
 * back into the dashboard-friendly shape so the parent component (table reload,
 * banner, retry selection) does not need to care that the backend now exposes
 * two endpoints instead of one.
 *
 * If an entire request fails (network / 5xx), every id of that task type is
 * flattened into `failed[]` with a generic reason so the user can retry the
 * partial selection without losing their work.
 *
 * @param {Object} params
 * @param {Map<string, { id: number, taskType: string }>} params.selection
 * @param {(result: { successful: { resolutionAttemptIds: number[], listingIds: number[] }, failed: Array<{ id: number, taskType: string, reason: string }> }) => void} params.onComplete
 */
const useBulkIgnoreController = ({ selection, onComplete }) => {
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const openConfirm = () => {
    setErrorMessage(null);
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (isSubmitting) return;
    setShowConfirm(false);
    setErrorMessage(null);
  };

  const dispatchAsPromise = (action, payload) =>
    new Promise((resolve, reject) => {
      dispatch(requestUtilityApi, {
        action,
        payload,
        onSuccess: resolve,
        onError: reject,
      });
    });

  // Stand-in for Promise.allSettled — the runtime polyfill (core-js@2)
  // doesn't include it. Returns the same shape so the merge below is
  // unchanged.
  const settle = (promise) =>
    promise.then(
      (value) => ({ status: 'fulfilled', value }),
      (reason) => ({ status: 'rejected', reason }),
    );

  const submit = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);

    const resolutionAttemptIds = [];
    const listingIds = [];
    for (const item of selection.values()) {
      if (item.taskType === TASK_TYPE_ADDRESS_RESOLUTION) {
        resolutionAttemptIds.push(item.id);
      } else if (item.taskType === TASK_TYPE_ADDRESS_VERIFICATION) {
        listingIds.push(item.id);
      }
    }

    const attemptsPromise = resolutionAttemptIds.length
      ? dispatchAsPromise(SR_API_ACTION_ADDRESS_RESOLUTION_ATTEMPTS_BULK_IGNORE, {
          ids: resolutionAttemptIds,
        })
      : Promise.resolve({ successful: [], failed: [] });

    const listingsPromise = listingIds.length
      ? dispatchAsPromise(SR_API_ACTION_LISTINGS_VERIFICATION_BULK_ARCHIVE, {
          listingIds,
        })
      : Promise.resolve({ successful: [], failed: [] });

    const [attemptsOutcome, listingsOutcome] = await Promise.all([
      settle(attemptsPromise),
      settle(listingsPromise),
    ]);

    const merged = {
      successful: {
        resolutionAttemptIds:
          attemptsOutcome.status === 'fulfilled' ? attemptsOutcome.value.successful : [],
        listingIds:
          listingsOutcome.status === 'fulfilled' ? listingsOutcome.value.successful : [],
      },
      failed: [
        ...(attemptsOutcome.status === 'fulfilled'
          ? attemptsOutcome.value.failed.map((f) => ({
              ...f,
              taskType: TASK_TYPE_ADDRESS_RESOLUTION,
            }))
          : resolutionAttemptIds.map((id) => ({
              id,
              taskType: TASK_TYPE_ADDRESS_RESOLUTION,
              reason: GENERIC_FAILURE_REASON,
            }))),
        ...(listingsOutcome.status === 'fulfilled'
          ? listingsOutcome.value.failed.map((f) => ({
              ...f,
              taskType: TASK_TYPE_ADDRESS_VERIFICATION,
            }))
          : listingIds.map((id) => ({
              id,
              taskType: TASK_TYPE_ADDRESS_VERIFICATION,
              reason: GENERIC_FAILURE_REASON,
            }))),
      ],
    };

    setIsSubmitting(false);

    const bothFailedHard =
      attemptsOutcome.status === 'rejected' && listingsOutcome.status === 'rejected';
    if (bothFailedHard) {
      const err = attemptsOutcome.reason || listingsOutcome.reason;
      setErrorMessage(err?.message || 'Failed to ignore addresses. Please try again.');
      // Modal stays open; selection intact so the user can retry.
      return;
    }

    setShowConfirm(false);
    onComplete(merged);
  };

  return {
    state: {
      isSubmitting,
      errorMessage,
      showConfirm,
      selectedCount: selection.size,
    },
    handlers: {
      openConfirm,
      closeConfirm,
      submit,
    },
  };
};

export default useBulkIgnoreController;
